/**
 * Integrytas Group - Inbound Sync Registry (ISR)
 * Page: ISR.TARGETS
 *
 * Register external polling targets (endpoint/auth/secret-ref/schedule + the UPP
 * IMPORT definition for transformation), see incremental state + last-sync, run
 * a poll on demand, and review the run log.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ISR.TARGETS';

registerUdcHost('ISR.TARGETS', 'isr_target', 'Inbound Sync Targets');

// Run-status codes → localized label + badge variant (§17.B11).
const RUN_STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  IDLE:    { labelKey: 'isr.status.IDLE',    label: 'Idle',    variant: 'neutral' },
  RUNNING: { labelKey: 'isr.status.RUNNING', label: 'Running', variant: 'info'    },
  OK:      { labelKey: 'isr.status.OK',      label: 'OK',      variant: 'success' },
  FAILED:  { labelKey: 'isr.status.FAILED',  label: 'Failed',  variant: 'danger'  },
  ERROR:   { labelKey: 'isr.status.ERROR',   label: 'Error',   variant: 'danger'  },
};

const TARGET_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 2 },
  { key: 'name', type: 'text', label: 'Name', col_span: 2 },
  { key: 'wsId', type: 'select', label: 'Workspace', required: true, col_span: 1 },
  { key: 'protocol', type: 'select', label: 'Protocol', col_span: 1 },
  { key: 'auth', type: 'select', label: 'Auth', col_span: 1 },
  { key: 'secretRef', type: 'text', label: 'Secret ref (env name)', col_span: 1 },
  { key: 'endpoint', type: 'text', label: 'Endpoint URL', col_span: 4 },
  { key: 'defId', type: 'select', label: 'UPP IMPORT definition', col_span: 2 },
  { key: 'schedKind', type: 'select', label: 'Schedule', col_span: 1 },
  { key: 'schedValue', type: 'text', label: 'Interval / Cron', col_span: 1 },
];
const TARGET_CORE_LAYOUT = toUldCoreLayout(TARGET_FIELD_DEFS);

const TARGET_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',      defaultWidth: 200 },
  { key: 'protocol',      header: 'Protocol',  defaultWidth: 110 },
  { key: 'schedule',      header: 'Schedule',  defaultWidth: 150 },
  { key: 'status',        header: 'Status',    defaultWidth: 130 },
  { key: 'last_sync',     header: 'Last sync', defaultWidth: 180 },
  { key: 'is_active',     header: 'Active',    defaultWidth: 90  },
];

interface TargetRow {
  isr_target_id: number; internal_code: string; name: string; une_workspace_id: number | null; protocol: string;
  endpoint_url: string; auth_mechanism: string; secret_ref: string | null; config: unknown; upp_definition_id: number | null;
  schedule_kind: string; cron_expr: string | null; interval_seconds: number; timeout_ms: number; is_active: 'Y' | 'N';
}
interface StateRow { isr_target_id: number; last_successful_sync_at: string | null; high_water_mark: string | null; run_status: string; consecutive_failures: number; last_error: string | null; next_run_at: string | null }
interface RunRow { isr_run_log_id: number; status: string; rows_fetched: number; rows_ingested: number; message: string | null; created_at: string }
interface WsLite { une_workspace_id: number; name: string }
interface DefLite { upp_definition_id: number; internal_code: string; name: string }

export interface IsrTargetsPageProps { session: AuthSession }

export function IsrTargetsPage({ session }: IsrTargetsPageProps) {
  return (
    <PersonalizationProvider pageCode="ISR.TARGETS" session={session}>
      <IsrTargetsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function IsrTargetsPageInner({ session }: IsrTargetsPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [states, setStates] = useState<Record<number, StateRow>>({});
  const [ws, setWs] = useState<WsLite[]>([]);
  const [defs, setDefs] = useState<DefLite[]>([]);
  const [editing, setEditing] = useState<TargetRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TargetRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the targets list.
  const [facets, setFacets] = useState<{ protocol: string[]; status: string[]; is_active: string[] }>(
    { protocol: [], status: [], is_active: ['Y'] },
  );
  const udcGrid = useUdcGrid('isr_target', 'ISR.TARGETS', { pkColumn: 'isr_target_id', rows: rows as unknown as Record<string, unknown>[] });
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A target's runs/state are loaded
  // in the modal, not per list row, so the panel is inert here.
  // TODO: wire relations (surface recent runs / state once loaded per row).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [t, s, w, d] = await Promise.all([
        apiDb.from('isr_target').select('isr_target_id, internal_code, name, une_workspace_id, protocol, endpoint_url, auth_mechanism, secret_ref, config, upp_definition_id, schedule_kind, cron_expr, interval_seconds, timeout_ms, is_active').order('internal_code'),
        apiDb.from('isr_state').select('isr_target_id, last_successful_sync_at, high_water_mark, run_status, consecutive_failures, last_error, next_run_at'),
        apiDb.from('une_workspace').select('une_workspace_id, name').eq('is_active', 'Y').order('name'),
        apiDb.from('upp_definition').select('upp_definition_id, internal_code, name').eq('direction', 'IMPORT').eq('is_active', 'Y').order('internal_code'),
      ]);
      if (t.error) throw t.error;
      setRows((t.data ?? []) as TargetRow[]);
      const sm: Record<number, StateRow> = {}; ((s.data ?? []) as StateRow[]).forEach(r => { sm[r.isr_target_id] = r; }); setStates(sm);
      setWs((w.data ?? []) as WsLite[]); setDefs((d.data ?? []) as DefLite[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const statusOf = (r: TargetRow) => states[r.isr_target_id]?.run_status ?? 'IDLE';

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort().map(v => ({ value: v, label: v }));
    return {
      protocol:  uniq(rows.map(r => r.protocol)),
      status:    uniq(rows.map(r => statusOf(r))),
      is_active: [{ value: 'Y', label: t('isr.facet.active', 'Active') }, { value: 'N', label: t('isr.facet.inactive', 'Inactive') }],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, states, t]);

  const facetsActive = facets.protocol.length + facets.status.length + facets.is_active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ protocol: [], status: [], is_active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesFacets = (r: TargetRow) =>
      (facets.protocol.length === 0  || facets.protocol.includes(r.protocol)) &&
      (facets.status.length === 0    || facets.status.includes(statusOf(r))) &&
      (facets.is_active.length === 0 || facets.is_active.includes(r.is_active));
    return rows.filter(t =>
      (!q || t.internal_code.toLowerCase().includes(q) || (t.name ?? '').toLowerCase().includes(q) || t.protocol.toLowerCase().includes(q))
      && matchesFacets(t)
      && udcGrid.matchRow(t as unknown as Record<string, unknown>));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, facets, states, udcGrid]);

  const handleDelete = async (r: TargetRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'isr_target', 'isr_target_id', r.isr_target_id);
      if (e) throw e;
      await reload();
      return true;
    } catch (e) { setError((e as Error).message); return false; } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('isr.title', 'Inbound Sync Targets')}
          description={t('isr.list.description', 'External systems the inbound-sync engine polls on a schedule, with their run state and last sync.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('isr.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('isr.new_target', 'New Target')}
              </button>)}
            </>
          }
        />
        {error && (
          <ErrorBanner
            message={t('isr.error_banner', "Couldn't complete the last inbound-sync operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('isr.filter_placeholder', 'Filter targets…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('isr.col.protocol', 'Protocol')} options={facetOptions.protocol}  selected={facets.protocol}  onChange={v => setFacets(f => ({ ...f, protocol: v }))} />
            <FacetFilter label={t('isr.col.status', 'Status')}     options={facetOptions.status}    selected={facets.status}    onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('isr.col.is_active', 'Active')}   options={facetOptions.is_active} selected={facets.is_active} onChange={v => setFacets(f => ({ ...f, is_active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('isr.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...TARGET_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('isr.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('isr.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('isr.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const st = states[row.isr_target_id];
                  return (
                    <Fragment key={row.isr_target_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(row.isr_target_id)} hasChildren={hasRelated(row.isr_target_id)} onToggle={() => detail.toggle(row.isr_target_id)} expandLabel={t('isr.related.expand', 'Show related records')} collapseLabel={t('isr.related.collapse', 'Hide related')} tooltip={t('isr.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'internal_code' && <span className="font-mono">{row.internal_code}</span>}
                          {col.key === 'protocol'      && <span className="text-csm-xs">{row.protocol}</span>}
                          {col.key === 'schedule'      && <span className="text-csm-2xs">{row.schedule_kind === 'CRON' ? row.cron_expr : `every ${row.interval_seconds}s`}</span>}
                          {col.key === 'status'        && (() => { const code = st?.run_status ?? 'IDLE'; const meta = RUN_STATUS_META[code]; return <><StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : code} />{st && st.consecutive_failures > 0 ? <span className="ml-1 text-csm-xs text-csm-danger">{` (${st.consecutive_failures}✗)`}</span> : null}</>; })()}
                          {col.key === 'last_sync'     && <span className="text-csm-2xs text-csm-watermark">{st?.last_successful_sync_at ? new Date(st.last_successful_sync_at).toLocaleString() : '—'}</span>}
                          {col.key === 'is_active'     && <YesNoBadge value={row.is_active} yesLabel={t('isr.facet.active', 'Active')} noLabel={t('isr.facet.inactive', 'Inactive')} />}
                          {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(row as unknown as Record<string, unknown>, col.key)}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                        {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(row); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('isr.edit', 'Edit')}</button>)}
                        {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(row); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('isr.delete', 'Delete')}</button>)}
                      </td>
                    </tr>
                    {detail.isOpen(row.isr_target_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(row.isr_target_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('isr.empty_title', 'No polling targets')}
                      action={
                        canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('isr.new_target_plus', '+ New Target')}
                        </button> : undefined
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <TargetModal session={session} row={editing} ws={ws} defs={defs}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('isr.delete_title', 'Delete Target?')}
          message={t('isr.delete_message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name || deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function TargetModal({ session, row, ws, defs, onClose, onSaved }: { session: AuthSession; row: TargetRow | null; ws: WsLite[]; defs: DefLite[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const id = row?.isr_target_id;

  const defaultConfig = { rows_path: 'data', delta_param: 'since', high_water_path: 'meta.max_id' };
  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    wsId: (row?.une_workspace_id ?? ws[0]?.une_workspace_id ?? null) as number | null,
    protocol: row?.protocol ?? 'HTTP',
    endpoint: row?.endpoint_url ?? '',
    auth: row?.auth_mechanism ?? 'NONE',
    secretRef: row?.secret_ref ?? '',
    config: JSON.stringify(row?.config ?? defaultConfig, null, 0),
    defId: row?.upp_definition_id ?? null,
    schedKind: row?.schedule_kind ?? 'INTERVAL',
    cron: row?.cron_expr ?? '',
    interval: row?.interval_seconds ?? 300,
    timeout: row?.timeout_ms ?? 30000,
    active: row ? row.is_active === 'Y' : false,
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Custom fields placed on this page's form become first-class FieldRenderer items:
  // they interleave with native fields and inherit the same personalization.
  const udcForm = useUdcFormFields('isr_target', 'ISR.TARGETS', { pkColumn: 'isr_target_id', pk: row?.isr_target_id });
  const targetCoreLayout = useMemo(() => [...TARGET_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const targetFieldDefs = useMemo(() => [...TARGET_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('ISR.TARGETS.MODAL', targetCoreLayout);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const set = (p: Partial<typeof form>) => setForm(f => ({ ...f, ...p }));

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.isr_target_id, isCreate, ws.length]);

  const loadRuns = async () => {
    if (!id) return;
    const { data } = await apiDb.from('isr_run_log').select('isr_run_log_id, status, rows_fetched, rows_ingested, message, created_at').eq('isr_target_id', id).order('created_at', { ascending: false }).limit(15);
    setRuns((data ?? []) as RunRow[]);
  };
  // loadRuns is re-created each render; reload only when the selected id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadRuns(); }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('isr.err.code_required', 'Code is required.'));
      if (!form.wsId) throw new Error(t('isr.err.workspace_required', 'Workspace is required.'));
      let cfg: unknown; try { cfg = JSON.parse(form.config || '{}'); } catch { throw new Error(t('isr.err.config_json', 'Config must be valid JSON.')); }
      const payload = { name: form.name.trim() || form.code.trim(), une_workspace_id: form.wsId, protocol: form.protocol, endpoint_url: form.endpoint.trim(), auth_mechanism: form.auth, secret_ref: form.secretRef || null, config: cfg, upp_definition_id: form.defId, schedule_kind: form.schedKind, cron_expr: form.cron || null, interval_seconds: form.interval, timeout_ms: form.timeout, is_active: form.active ? 'Y' : 'N' };
      let code = form.code.trim().toUpperCase();
      if (isCreate) { const { error: e } = await routedInsert(session, 'isr_target', { internal_code: code, ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'isr_target', 'isr_target_id', id, payload); if (e) throw e; code = row?.internal_code ?? code; }
      // (re)register the cron job for CRON-scheduled targets
      try { await apiDb.rpc('isr_set_schedule', { p_code: code }); } catch { /* pg_cron optional */ }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!row) return; setBusy(true); setError(null); setNotice(null);
    try {
      const { error: e } = await apiDb.rpc('isr_dispatch_one', { p_target_id: row.isr_target_id });
      if (e) throw e;
      setNotice(t('isr.notice.dispatched', 'Poll dispatched (the worker runs asynchronously; refresh the run log shortly).'));
      setTimeout(() => void loadRuns(), 1500);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const modalTitle = isCreate ? t('isr.modal.create_title', 'Create Inbound Sync Target') : t('isr.modal.edit_title', 'Edit Target — {{code}}', { code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('isr.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={targetFieldDefs}
            layout={layout}
            columns={4}
            pageCode="ISR.TARGETS.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label="Code">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.code', 'Code')}</label>
                  <input value={form.code} disabled={!isCreate}
                         onChange={e => set({ code: e.target.value.toUpperCase() })}
                         onBlur={() => {
                           if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                             setInternalCodeError(t('isr.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                           } else {
                             setInternalCodeError(null);
                           }
                         }}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('isr.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                  }
                </>
                </PersonalizedField>
              );
              if (f.key === 'name') return (
                <PersonalizedField componentKey="modal" fieldKey="name" label="Name">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.name', 'Name')}</label>
                  <input value={form.name} onChange={e => set({ name: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></>
                </PersonalizedField>
              );
              if (f.key === 'wsId') return (
                <PersonalizedField componentKey="modal" fieldKey="wsId" label="Workspace">
                  <FilteredModalSingleSelect
                    label={t('isr.field.workspace', 'Workspace')}
                    options={ws.map(w => ({ internal_code: String(w.une_workspace_id), display_text: w.name }))}
                    selectedCode={form.wsId == null ? null : String(form.wsId)}
                    onChange={(code) => set({ wsId: code ? Number(code) : null })}
                    allowClear={false}
                  />
                </PersonalizedField>
              );
              if (f.key === 'protocol') return (
                <PersonalizedField componentKey="modal" fieldKey="protocol" label="Protocol">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.protocol', 'Protocol')}</label>
                  <select value={form.protocol} onChange={e => set({ protocol: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm"><option value="HTTP">HTTP</option><option value="SFTP">SFTP</option></select></>
                </PersonalizedField>
              );
              if (f.key === 'auth') return (
                <PersonalizedField componentKey="modal" fieldKey="auth" label="Auth">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.auth', 'Auth')}</label>
                  <select value={form.auth} onChange={e => set({ auth: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{['NONE', 'BEARER', 'BASIC', 'API_KEY', 'SSH_KEY'].map(a => <option key={a} value={a}>{a}</option>)}</select></>
                </PersonalizedField>
              );
              if (f.key === 'secretRef') return (
                <PersonalizedField componentKey="modal" fieldKey="secretRef" label="Secret ref (env name)">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.secret_ref', 'Secret ref (env name)')}</label>
                  <input value={form.secretRef} onChange={e => set({ secretRef: e.target.value })} placeholder="ACME_API_KEY" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></>
                </PersonalizedField>
              );
              if (f.key === 'endpoint') return (
                <PersonalizedField componentKey="modal" fieldKey="endpoint" label="Endpoint URL">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.endpoint', 'Endpoint URL')}</label>
                  <input value={form.endpoint} onChange={e => set({ endpoint: e.target.value })} placeholder="https://api.acme.com/v1/orders" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></>
                </PersonalizedField>
              );
              if (f.key === 'defId') return (
                <PersonalizedField componentKey="modal" fieldKey="defId" label="UPP IMPORT definition">
                  <FilteredModalSingleSelect
                    label={t('isr.field.def', 'UPP IMPORT definition')}
                    options={defs.map(d => ({ internal_code: String(d.upp_definition_id), display_text: d.internal_code }))}
                    selectedCode={form.defId == null ? null : String(form.defId)}
                    onChange={(code) => set({ defId: code ? Number(code) : null })}
                    clearLabel={t('isr.none_option', '— none —')}
                  />
                </PersonalizedField>
              );
              if (f.key === 'schedKind') return (
                <PersonalizedField componentKey="modal" fieldKey="schedKind" label="Schedule">
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.schedule', 'Schedule')}</label>
                  <select value={form.schedKind} onChange={e => set({ schedKind: e.target.value })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm"><option value="INTERVAL">INTERVAL</option><option value="CRON">CRON</option></select></>
                </PersonalizedField>
              );
              if (f.key === 'schedValue') return (
                <PersonalizedField componentKey="modal" fieldKey="schedValue" label="Interval / Cron">
                {form.schedKind === 'CRON'
                  ? <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.cron', 'Cron')}</label><input value={form.cron} onChange={e => set({ cron: e.target.value })} placeholder="*/15 * * * *" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></>
                  : <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.field.interval', 'Interval (s)')}</label><input type="number" value={form.interval} onChange={e => set({ interval: Number(e.target.value) })} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></>}
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>
        <div className="mb-3"><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('isr.config_label', 'Config JSON (rows_path, delta_param, high_water_path, cursor_param, headers, method…)')}</label>
          <textarea rows={3} value={form.config} onChange={e => set({ config: e.target.value })} className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" /></div>

        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} className="h-4 w-4 accent-gray-700" /> {t('isr.active', 'Active')}</label>
          {!isCreate && <button onClick={() => void runNow()} disabled={busy} className="h-8 rounded border border-csm-border bg-white px-3 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('isr.run_now', 'Run Now')}</button>}
          <button onClick={() => void save()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('isr.saving', 'Saving…') : t('isr.save', 'Save')}</button>
        </div>
        {notice && <div className="mb-2 rounded border border-csm-border bg-csm-surface-alt px-2 py-1 text-csm-xs text-csm-ink-soft">{notice}</div>}

        {!isCreate && (
          <div className="border-t border-csm-border pt-2">
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('isr.recent_runs', 'Recent Runs')}</div>
            <table className="min-w-full border-collapse text-csm-2xs">
              <thead className="uppercase text-csm-ink-soft"><tr><th className="px-2 text-left">{t('isr.run.when', 'When')}</th><th className="px-2 text-left">{t('isr.run.status', 'Status')}</th><th className="px-2 text-right csm-num">{t('isr.run.fetched', 'Fetched')}</th><th className="px-2 text-right csm-num">{t('isr.run.ingested', 'Ingested')}</th><th className="px-2 text-left">{t('isr.run.message', 'Message')}</th></tr></thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.isr_run_log_id} className={r.status !== 'OK' ? 'text-csm-danger' : ''}>
                    <td className="px-2 py-0.5 text-csm-watermark">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-2 py-0.5">{(() => { const meta = RUN_STATUS_META[r.status]; return <StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : r.status} />; })()}</td>
                    <td className="px-2 py-0.5 text-right csm-num">{r.rows_fetched}</td>
                    <td className="px-2 py-0.5 text-right csm-num">{r.rows_ingested}</td>
                    <td className="px-2 py-0.5 font-mono">{r.message ?? ''}</td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={5} className="px-2 py-1 italic text-csm-watermark">{t('isr.no_runs', 'No runs yet.')}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {error &&<div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('isr.create_hint', 'Save first to run a poll + view the run log. Set ISR.WORKER_URL + the secret env var for dispatch.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('isr.discard_title', 'Discard changes?')}
          message={t('isr.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('isr.discard_confirm', 'Discard')}
          cancelLabel={t('isr.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
